/**
 * Affordability Calculator Service
 *
 * VERBATIM PORT of `calculateMaxLoan` from the consultant app
 * (`src/services/calculator/bondCalculator.ts`). The public website's
 * Affordability Calculator must produce EXACTLY the same numbers as the
 * consultant Investment Preparation tool's Affordability tab, so this
 * function body must be kept byte-identical with the source. If the
 * consultant calculator's methodology changes, change it here too.
 *
 * Methodology (Ooba): max monthly payment = Net Surplus Income
 * (gross − deductions − expenses), capped at 30% of gross income,
 * reverse-PMT'd into a maximum loan amount.
 */

export interface AffordabilityInput {
  grossIncome: number;
  salaryDeductions: number;
  monthlyExpenses: number;
  interestRate: number;
  loanTermYears: number;
}

export interface AffordabilityResult {
  maxLoanAmount: number;
  maxMonthlyPayment: number;
  netIncome: number;
  disposableIncome: number;
  affordabilityRatio: number;
  adjustedRatio: number;
}

/**
 * Calculate maximum affordable loan based on income and expenses
 * Uses Ooba's exact calculation methodology
 */
export function calculateMaxLoan(input: AffordabilityInput): AffordabilityResult {
  const { grossIncome, salaryDeductions, monthlyExpenses, interestRate, loanTermYears } = input;

  // Calculate net and disposable income (Ooba calls this "Net Surplus Income")
  const netIncome = grossIncome - salaryDeductions;
  const disposableIncome = netIncome - monthlyExpenses; // This is the Net Surplus

  // OOBA METHODOLOGY: Use 100% of net surplus as maximum payment
  // But cap at 30% of gross income for safety (Ooba's standard cap)
  let maxMonthlyPayment = disposableIncome;

  // Apply Ooba's safety cap: Never exceed 30% of gross income
  const grossIncomeCap = grossIncome * 0.30;
  if (maxMonthlyPayment > grossIncomeCap) {
    maxMonthlyPayment = grossIncomeCap;
  }

  // Ensure we don't have negative payment
  if (maxMonthlyPayment < 0) {
    maxMonthlyPayment = 0;
  }

  // Calculate the actual ratio being used (for display purposes)
  const actualRatio = grossIncome > 0 ? maxMonthlyPayment / grossIncome : 0;

  // Use reverse PMT formula to calculate maximum loan amount
  const monthlyRate = interestRate / 12;
  const numPayments = loanTermYears * 12;

  let maxLoanAmount: number;
  if (monthlyRate === 0) {
    maxLoanAmount = maxMonthlyPayment * numPayments;
  } else {
    // Standard PMT reverse calculation
    maxLoanAmount = maxMonthlyPayment * ((1 - Math.pow(1 + monthlyRate, -numPayments)) / monthlyRate);
  }

  // Round to nearest rand for accuracy
  maxLoanAmount = Math.round(maxLoanAmount);

  return {
    maxLoanAmount,
    maxMonthlyPayment: Math.round(maxMonthlyPayment),
    netIncome,
    disposableIncome, // This is the Net Surplus Income
    affordabilityRatio: actualRatio,
    adjustedRatio: actualRatio
  };
}

/**
 * Format currency for display — same Intl options as the consultant app's
 * `formatCurrency` so amounts render identically (e.g. "R 12 500").
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
}
